import React, { useState } from 'react';
import './css/ObjectPermissionMatrix.css';

/** Single object permission shape: can_create, can_read, can_update, can_delete, view_all, modify_all */
const DEFAULT_PERM = {
  can_create: false,
  can_read: false,
  can_update: false,
  can_delete: false,
  view_all: false,
  modify_all: false,
};

/** Apply permission dependency logic (same as PermissionBulkEdit) */
function applyPermissionDeps(perm, permissionType, value) {
  const updated = { ...perm, [permissionType]: value };
  if (permissionType === 'view_all' && value === true) {
    updated.can_read = true;
  } else if (permissionType === 'modify_all' && value === true) {
    updated.can_read = true;
    updated.can_create = true;
    updated.can_update = true;
    updated.can_delete = true;
    updated.view_all = true;
  } else if (permissionType === 'can_read' && value === false) {
    updated.can_create = false;
    updated.can_update = false;
    updated.can_delete = false;
    updated.view_all = false;
    updated.modify_all = false;
  } else if ((permissionType === 'can_create' || permissionType === 'can_update' || permissionType === 'can_delete') && value === false) {
    updated.modify_all = false;
  } else if ((permissionType === 'can_create' || permissionType === 'can_update' || permissionType === 'can_delete') && value === true) {
    updated.can_read = true;
  }
  return updated;
}

/**
 * Object permission matrix (Create, Read, Update, Delete, View All, Modify All per object).
 * Same UI and logic as PermissionBulkEdit. Used at plan level and plan license level.
 *
 * @param {Array<{object_name: string, object_display_name?: string}>} objects - Objects to show
 * @param {Object} permissions - { object_name: { can_create, can_read, ... } }
 * @param {Function} onChange - (updatedPermissions) => void
 * @param {boolean} disabled - Disable editing
 * @param {Object} ceilingPermissions - Optional ceiling: { object_name: { can_create, view_all, modify_all, ... } }.
 *   If provided, a checkbox is disabled when the ceiling does not allow that permission (e.g. subscription removed view_all).
 * @param {boolean} showSearch - Show search input (default true)
 * @param {string} className - Optional CSS class
 */
const ObjectPermissionMatrix = ({
  objects = [],
  permissions = {},
  onChange,
  disabled = false,
  ceilingPermissions,
  showSearch = true,
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const getPerm = (objectName) => ({
    ...DEFAULT_PERM,
    ...(permissions[objectName] || {}),
  });

  const handleChange = (objectName, permissionType, value) => {
    const current = getPerm(objectName);
    const updated = applyPermissionDeps(current, permissionType, value);
    onChange({ ...permissions, [objectName]: updated });
  };

  const handleSelectAll = (permissionType, value) => {
    const updated = { ...permissions };
    filteredObjects.forEach((obj) => {
      if (ceilingPermissions?.[obj.object_name]?.[permissionType] === false) return;
      const current = getPerm(obj.object_name);
      updated[obj.object_name] = applyPermissionDeps(current, permissionType, value);
    });
    onChange(updated);
  };

  const isCellDisabled = (objectName, permissionType) =>
    disabled || (ceilingPermissions?.[objectName]?.[permissionType] === false);

  const filteredObjects = objects.filter(
    (o) =>
      (o.object_display_name || o.object_name || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (o.object_name || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const areAllSelected = (permissionType) =>
    filteredObjects.length > 0 &&
    filteredObjects.every((o) => getPerm(o.object_name)[permissionType] === true);

  const areSomeSelected = (permissionType) => {
    if (filteredObjects.length === 0) return false;
    const count = filteredObjects.filter((o) => getPerm(o.object_name)[permissionType] === true).length;
    return count > 0 && count < filteredObjects.length;
  };

  const COLUMNS = [
    { key: 'can_create', label: 'Create' },
    { key: 'can_read', label: 'Read' },
    { key: 'can_update', label: 'Update' },
    { key: 'can_delete', label: 'Delete' },
    { key: 'view_all', label: 'View All' },
    { key: 'modify_all', label: 'Modify All' },
  ];

  if (objects.length === 0) {
    return (
      <div className={`object-permission-matrix ${className}`}>
        <div className="opm-empty">
          <p>No objects configured. Configure Allowed Objects Permission first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`object-permission-matrix ${className}`}>
      {showSearch && (
        <div className="opm-search">
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Search objects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="opm-search-input"
          />
        </div>
      )}
      <div className="opm-table-wrap">
        <table className="opm-table">
          <thead>
            <tr>
              <th className="opm-col-object">Object</th>
                  {COLUMNS.map((col) => {
                const headerDisabled = disabled || filteredObjects.every((o) => isCellDisabled(o.object_name, col.key));
                return (
                  <th key={col.key} className="opm-col-checkbox">
                    <div className="opm-header-checkbox">
                      {!headerDisabled && (
                        <input
                          type="checkbox"
                          checked={areAllSelected(col.key)}
                          ref={(el) => {
                            if (el) el.indeterminate = areSomeSelected(col.key);
                          }}
                          onChange={(e) => handleSelectAll(col.key, e.target.checked)}
                          className="opm-header-cb"
                        />
                      )}
                      <span>{col.label}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredObjects.map((obj) => {
              const perm = getPerm(obj.object_name);
              const displayName = obj.object_display_name || obj.object_name?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || obj.object_name;
              return (
                <tr key={obj.object_name}>
                  <td className="opm-col-object">
                    <div className="opm-object-name">{displayName}</div>
                  </td>
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="opm-col-checkbox">
                      <input
                        type="checkbox"
                        checked={perm[col.key] || false}
                        onChange={(e) => handleChange(obj.object_name, col.key, e.target.checked)}
                        disabled={isCellDisabled(obj.object_name, col.key)}
                        className="opm-checkbox"
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ObjectPermissionMatrix;
